import React, { useEffect, useRef, useState, useCallback } from "react";
import { Search, Send, Loader2, MessageCircle, ArrowLeft, X } from "lucide-react";
import { toast } from "sonner";
import api, { timeAgo } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";

const Messages = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const bottomRef = useRef(null);
  const activeIdRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/dm/conversations");
      setConversations(data.conversations || []);
    } catch (e) { toast.error("Could not load conversations"); }
    finally { setLoadingConvs(false); }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const openConversation = useCallback(async (conv) => {
    setActive(conv);
    activeIdRef.current = conv.id;
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/dm/conversations/${conv.id}`);
      setMessages(data.messages || []);
      if (data.conversation) setActive(data.conversation);
    } catch (e) { toast.error("Could not open conversation"); }
    finally { setLoadingThread(false); }
  }, []);

  useEffect(() => {
    if (!active) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/dm/conversations/${activeIdRef.current}`);
        setMessages(data.messages || []);
      } catch (e) {}
      loadConversations();
    }, 5000);
    return () => clearInterval(iv);
  }, [active, loadConversations]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !active) return;
    const text = draft.trim();
    setDraft("");
    setSending(true);
    try {
      const { data } = await api.post(`/dm/conversations/${active.id}/messages`, { message: text });
      if (data && data.id) setMessages((p) => [...p, data]);
      else { const r = await api.get(`/dm/conversations/${active.id}`); setMessages(r.data.messages || []); }
      loadConversations();
    } catch (err) { toast.error("Message failed to send"); setDraft(text); }
    finally { setSending(false); }
  };

  useEffect(() => {
    if (!searchOpen) return;
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/dm/users/search?q=${encodeURIComponent(query.trim())}`); setResults(data.users || []); }
      catch (e) { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query, searchOpen]);

  const startDm = async (target) => {
    try {
      const { data } = await api.post("/dm/start", { target_user_id: target.id });
      setSearchOpen(false); setQuery(""); setResults([]);
      await loadConversations();
      if (data.conversation) openConversation(data.conversation);
    } catch (e) { toast.error("Could not start conversation"); }
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex">
      <div className={`w-full md:w-80 border-r border-border bg-card/50 flex flex-col ${active ? "hidden md:flex" : "flex"}`}>
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold">Messages</h2>
          <button data-testid="new-message-button" onClick={() => setSearchOpen(true)}
            className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-atlantic active:scale-95 transition-all" title="New message">
            <Send className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loadingConvs ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : conversations.length === 0 ? (
            <div className="text-center px-6 py-12 text-muted-foreground">
              <MessageCircle className="w-8 h-8 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No conversations yet. Start one with the send button.</p>
            </div>
          ) : conversations.map((c) => (
            <button key={c.id} data-testid="conversation-item" onClick={() => openConversation(c)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary transition-colors border-b border-border/50 ${active?.id === c.id ? "bg-secondary" : ""}`}>
              <Avatar name={c.other_user_name} src={c.other_user_avatar} size={44} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold truncate">{c.other_user_name}</p>
                  <span className="text-xs text-muted-foreground shrink-0">{timeAgo(c.last_message_at)}</span>
                </div>
                <p className="text-sm text-muted-foreground truncate">{c.last_message || "Say hello"}</p>
              </div>
              {c.unread_count > 0 && <span className="w-5 h-5 rounded-full bg-brass text-accent-foreground text-xs font-semibold flex items-center justify-center">{c.unread_count}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className={`flex-1 flex-col ${active ? "flex" : "hidden md:flex"}`}>
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <MessageCircle className="w-12 h-12 mb-4 opacity-40" />
            <p>Select a conversation to start chatting</p>
          </div>
        ) : (
          <>
            <div className="h-16 border-b border-border flex items-center gap-3 px-4 bg-card/60">
              <button className="md:hidden" onClick={() => setActive(null)} data-testid="back-to-conversations"><ArrowLeft className="w-5 h-5" /></button>
              <Avatar name={active.other_user_name} src={active.other_user_avatar} size={40} />
              <p className="font-semibold" data-testid="thread-title">{active.other_user_name}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
              {loadingThread ? (
                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : messages.map((m) => {
                const mine = m.is_mine ?? m.sender_id === user?.id;
                return (
                  <div key={m.id} data-testid="message-bubble" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 leading-relaxed ${mine ? "bg-primary text-primary-foreground rounded-2xl rounded-br-md" : "bg-secondary text-foreground rounded-2xl rounded-bl-md"}`}>
                      <p className="whitespace-pre-wrap break-words">{m.message}</p>
                      <p className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={send} className="p-3 sm:p-4 border-t border-border bg-card/60 flex items-center gap-2">
              <input data-testid="message-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Write a message…"
                className="flex-1 px-4 py-2.5 rounded-full border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow" />
              <button data-testid="send-message-button" type="submit" disabled={sending || !draft.trim()}
                className="w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-atlantic active:scale-95 transition-all disabled:opacity-50 shrink-0">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </>
        )}
      </div>

      {searchOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4">
          <div className="absolute inset-0 bg-nightsea/70 backdrop-blur-sm" onClick={() => setSearchOpen(false)} />
          <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-xl animate-fade-up overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading text-lg font-semibold">New message</h3>
              <button onClick={() => setSearchOpen(false)} data-testid="close-search"><X className="w-5 h-5 text-muted-foreground" /></button>
            </div>
            <div className="p-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input data-testid="user-search-input" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search members by name…"
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow" />
              </div>
              <div className="mt-3 max-h-72 overflow-y-auto">
                {searching ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : results.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">{query ? "No members found" : "Type a name to search"}</p>
                ) : results.map((u) => (
                  <button key={u.id} data-testid="user-search-result" onClick={() => startDm(u)}
                    className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-secondary transition-colors text-left">
                    <Avatar name={u.display_name} src={u.avatar_base64} size={40} />
                    <span className="font-medium">{u.display_name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
